// frontend/src/utils/exportSecuenciaExcel.ts
// Export con formato del Excel de Secuencia de Producción: una hoja por línea
// (estilo de la página: cabecera sólida, bandeado, y celdas Estado/Granel/
// Componentes coloreadas como los badges) + una hoja de Resumen.
import ExcelJS from 'exceljs'
import type { TFunction } from 'i18next'
import type { ProductionOrder, LineId, OrderStatus, ComponentStatus } from '@/types/productionSequence'
import { fmtDate, COMP_COLOR } from '@/components/production-sequence/sequenceFormat'

type Fill = { bg: string; font: string }

// Paleta alineada con sequenceFormat (badges de la página), en ARGB.
const STATUS_FILL: Record<OrderStatus, Fill> = {
  LIBERADO:             { bg: 'FFD1FAE5', font: 'FF047857' }, // emerald
  INICIADO:             { bg: 'FFD1FAE5', font: 'FF047857' }, // emerald
  PROGRAMADO:           { bg: 'FFFEF3C7', font: 'FFB45309' }, // amber
  TERMINADO:            { bg: 'FFE2E8F0', font: 'FF475569' }, // slate
  NOTIFICADO_TERMINADO: { bg: 'FFEDE9FE', font: 'FF6D28D9' }, // violet
  ESTIMADO:             { bg: 'FFF1F5F9', font: 'FF64748B' }, // slate claro
}
// El mismo vocabulario de color que la pantalla (`COMP_COLOR`), traducido a
// ARGB. La correspondencia la vigila `sequencePaleta.test.ts`: un estado que
// cambie de color en la pantalla y no aquí rompe el test, no el Excel del
// cliente.
export const ARGB_POR_COLOR = {
  emerald: { bg: 'FFD1FAE5', font: 'FF047857' },
  violet:  { bg: 'FFEDE9FE', font: 'FF6D28D9' },
  sky:     { bg: 'FFE0F2FE', font: 'FF0369A1' },
  orange:  { bg: 'FFFFEDD5', font: 'FFC2410C' },
  red:     { bg: 'FFFEE2E2', font: 'FFB91C1C' },
} as const

const SEMA_FILL: Record<ComponentStatus, Fill> = {
  OK:               ARGB_POR_COLOR[COMP_COLOR.OK],
  PENDIENTE_CALIDAD: ARGB_POR_COLOR[COMP_COLOR.PENDIENTE_CALIDAD],
  ENTRE_ALMACENES:  ARGB_POR_COLOR[COMP_COLOR.ENTRE_ALMACENES],
  COMPRAS_EN_CURSO: ARGB_POR_COLOR[COMP_COLOR.COMPRAS_EN_CURSO],
  ROTURA_STOCK:     ARGB_POR_COLOR[COMP_COLOR.ROTURA_STOCK],
}
const GRANEL_FILL: Record<'OK' | 'PENDIENTE', Fill> = {
  OK:        { bg: 'FFD1FAE5', font: 'FF047857' },
  PENDIENTE: { bg: 'FFF1F5F9', font: 'FF64748B' },
}
const HEADER: Fill = { bg: 'FF0284C7', font: 'FFFFFFFF' } // sky-600
const BAND = 'FFF8FAFC'     // slate-50
const BORDER = 'FFE2E8F0'   // slate-200

type ColKind = 'estado' | 'granel' | 'comp' | 'num' | 'text'
interface Col {
  header: string
  width: number
  kind: ColKind
  key: string
  value: (o: ProductionOrder, i: number) => string | number | null
}

function columns(t: TFunction): Col[] {
  const c = (k: string) => t(`sequence.col.${k}`)
  return [
    { header: '#', width: 5, kind: 'num', key: 'num', value: (_o, i) => i + 1 },
    { header: c('ot'), width: 15, kind: 'text', key: 'ot', value: o => o.id },
    { header: c('referencia'), width: 16, kind: 'text', key: 'referencia', value: o => o.referencia },
    { header: c('descripcion'), width: 38, kind: 'text', key: 'descripcion', value: o => o.descripcion },
    { header: c('familia'), width: 18, kind: 'text', key: 'familia', value: o => o.familia },
    { header: c('subfamilia'), width: 18, kind: 'text', key: 'subfamilia', value: o => o.subfamilia },
    { header: c('acabado'), width: 9, kind: 'text', key: 'acabado', value: o => t(`sequence.acabado.${o.acabado}`) },
    { header: c('refNueva'), width: 10, kind: 'text', key: 'refNueva', value: o => (o.esReferenciaNueva ? t('sequence.refNueva.yes') : t('sequence.refNueva.no')) },
    { header: c('estado'), width: 16, kind: 'estado', key: 'estado', value: o => t(`sequence.estado.${o.estado}`) },
    { header: c('udOT'), width: 11, kind: 'num', key: 'udOT', value: o => o.udOT },
    { header: c('udProd'), width: 11, kind: 'num', key: 'udProd', value: o => o.udProducidas },
    { header: c('udPte'), width: 11, kind: 'num', key: 'udPte', value: o => o.udPendientes },
    { header: c('adherencia'), width: 12, kind: 'num', key: 'adherencia', value: o => (o.adherenciaPct != null ? Math.round(o.adherenciaPct) : null) },
    { header: c('granel'), width: 14, kind: 'granel', key: 'granel', value: o => t(`sequence.granel.${o.granelStatus}`) },
    { header: c('componentes'), width: 17, kind: 'comp', key: 'componentes', value: o => t(`sequence.componentStatus.${o.componentesStatus}`) },
    { header: c('fPedido'), width: 11, kind: 'text', key: 'fPedido', value: o => fmtDate(o.fechaPedido) },
    { header: c('fPlanFin'), width: 12, kind: 'text', key: 'fPlanFin', value: o => fmtDate(o.fechaPlanificadaFin) },
    { header: c('fRealFin'), width: 11, kind: 'text', key: 'fRealFin', value: o => fmtDate(o.fechaRealFin) },
    { header: c('upm'), width: 8, kind: 'num', key: 'upm', value: o => o.upm },
    { header: c('udTurno'), width: 10, kind: 'num', key: 'udTurno', value: o => o.udTurnoPromedio },
    { header: c('nOp'), width: 7, kind: 'num', key: 'nOp', value: o => o.nOperarios },
    { header: c('calidad'), width: 8, kind: 'text', key: 'calidad', value: o => (o.campoCalidad === true ? '✓' : '') },
    { header: c('comentarios'), width: 30, kind: 'text', key: 'comentarios', value: o => o.comentarios },
    { header: c('motivoDesv'), width: 30, kind: 'text', key: 'motivoDesv', value: o => o.motivoDesviacion },
  ]
}

function safeSheetName(name: string, used: Set<string>): string {
  let base = (name || '—').replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 28) || '—'
  let n = base
  let i = 2
  while (used.has(n.toLowerCase())) n = `${base} ${i++}`.slice(0, 31)
  used.add(n.toLowerCase())
  return n
}

function thinBorder() {
  const side = { style: 'thin' as const, color: { argb: BORDER } }
  return { top: side, left: side, bottom: side, right: side }
}

function fill(argb: string): ExcelJS.Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } }
}

function buildSheet(ws: ExcelJS.Worksheet, cols: Col[], orders: ProductionOrder[]) {
  ws.columns = cols.map(c => ({ width: c.width }))
  ws.views = [{ state: 'frozen', ySplit: 1 }]

  // Cabecera
  const headerRow = ws.addRow(cols.map(c => c.header))
  headerRow.height = 22
  headerRow.eachCell(cell => {
    cell.fill = fill(HEADER.bg)
    cell.font = { bold: true, color: { argb: HEADER.font }, size: 11 }
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
    cell.border = thinBorder()
  })

  // Filas
  orders.forEach((o, i) => {
    const row = ws.addRow(cols.map(col => col.value(o, i) ?? ''))
    const banded = i % 2 === 1
    row.eachCell((cell, colNumber) => {
      const col = cols[colNumber - 1]
      cell.border = thinBorder()
      cell.alignment = { vertical: 'middle', horizontal: col.kind === 'num' ? 'right' : 'left', wrapText: false }
      if (banded) cell.fill = fill(BAND)
      if (col.kind === 'estado') {
        const f = STATUS_FILL[o.estado]
        if (f) { cell.fill = fill(f.bg); cell.font = { color: { argb: f.font }, bold: true } }
      } else if (col.kind === 'comp') {
        const f = SEMA_FILL[o.componentesStatus]
        if (f) { cell.fill = fill(f.bg); cell.font = { color: { argb: f.font }, bold: true } }
      } else if (col.kind === 'granel') {
        const f = GRANEL_FILL[o.granelStatus]
        if (f) { cell.fill = fill(f.bg); cell.font = { color: { argb: f.font }, bold: true } }
      }
    })
  })

  // Autofiltro sobre la cabecera
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: cols.length } }
}

function buildResumen(ws: ExcelJS.Worksheet, t: TFunction, byLine: Map<LineId, ProductionOrder[]>) {
  ws.columns = [{ width: 26 }, { width: 12 }, { width: 14 }, { width: 16 }]
  const header = ws.addRow([
    t('sequence.excel.resLine'), t('sequence.excel.resOrders'),
    t('sequence.excel.resUdOT'), t('sequence.excel.resUdPte'),
  ])
  header.height = 22
  header.eachCell(cell => {
    cell.fill = fill(HEADER.bg)
    cell.font = { bold: true, color: { argb: HEADER.font }, size: 11 }
    cell.alignment = { vertical: 'middle', horizontal: 'center' }
    cell.border = thinBorder()
  })
  let i = 0
  let totOrders = 0, totUdOT = 0, totUdPte = 0
  for (const [line, orders] of byLine) {
    const udOT = orders.reduce((s, o) => s + o.udOT, 0)
    const udPte = orders.reduce((s, o) => s + o.udPendientes, 0)
    totOrders += orders.length; totUdOT += udOT; totUdPte += udPte
    const row = ws.addRow([line, orders.length, udOT, udPte])
    const banded = i % 2 === 1
    row.eachCell((cell, n) => {
      cell.border = thinBorder()
      cell.alignment = { horizontal: n === 1 ? 'left' : 'right' }
      if (banded) cell.fill = fill(BAND)
    })
    i++
  }
  const total = ws.addRow([t('sequence.excel.resTotal'), totOrders, totUdOT, totUdPte])
  total.eachCell((cell, n) => {
    cell.font = { bold: true }
    cell.border = thinBorder()
    cell.alignment = { horizontal: n === 1 ? 'left' : 'right' }
  })
  ws.views = [{ state: 'frozen', ySplit: 1 }]
}

/** Claves de columna visibles para el rol AVP (sin la columna '#'). */
export const AVP_EXPORT_KEYS = [
  'ot', 'referencia', 'descripcion', 'udOT', 'udProd', 'udPte', 'fPlanFin', 'fRealFin',
] as const

const AVP_EXPORT_SET = new Set<string>(AVP_EXPORT_KEYS)

/** Construye el workbook (una hoja por línea + Resumen). Puro/testeable. */
export function buildSecuenciaWorkbook(orders: ProductionOrder[], t: TFunction, avpOnly = false): ExcelJS.Workbook {
  const allCols = columns(t)
  const cols = avpOnly ? allCols.filter(c => AVP_EXPORT_SET.has(c.key)) : allCols
  // Agrupar por línea, preservando el orden de secuencia (orders ya viene ordenado).
  const byLine = new Map<LineId, ProductionOrder[]>()
  for (const o of orders) {
    const arr = byLine.get(o.lineId) ?? []
    arr.push(o)
    byLine.set(o.lineId, arr)
  }

  const wb = new ExcelJS.Workbook()
  wb.creator = 'Airval Factory'

  buildResumen(wb.addWorksheet(t('sequence.excel.resumenSheet')), t, byLine)

  const used = new Set<string>()
  for (const [line, lineOrders] of byLine) {
    const ws = wb.addWorksheet(safeSheetName(String(line), used))
    buildSheet(ws, cols, lineOrders)
  }
  return wb
}

/** Construye y descarga el Excel con una hoja por línea + Resumen. */
export async function exportSecuenciaExcel(orders: ProductionOrder[], t: TFunction, filenameBase: string, avpOnly = false): Promise<void> {
  const wb = buildSecuenciaWorkbook(orders, t, avpOnly)
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filenameBase}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}
